import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { sortBy } from "lodash";
import classnames from "classnames";

import { Context as DataSourceContext } from "../DataSourceContext";
import { Context as InteractionContext } from "../Interaction/InteractionContext";
import PositionableList from "./List";
import { MapEditorRouteContext } from "../../CustomRoutes/MapEditor";
import CalibrateMap from "../CalibrateMap/CalibrateMap";
import { isMapVersionLockedType } from "../mapVersionLock";

const toggle = (activeType, nextType, setActive) => {
  if (activeType === nextType) {
    setActive(null);
  } else {
    setActive(nextType);
  }
};

const positionableType2PageName = {
  findings: "Findings",
  control_measure: "Project Legend Item",
  pollutant: "Project Legend Item",
  commentables: "Annotations",
  asset_type: "Map Drawing",
};

const actionsWithAllPerm = {
  read: "all",
  create: "all",
  update: "all",
};

/**
 * Render collapsible list for each data source type
 */
function PositionableNavigation(props) {
  const mapEditorRouteContext = useContext(MapEditorRouteContext);
  const dataSourceContext = useContext(DataSourceContext);
  const interactionContext = useContext(InteractionContext);
  const { className, permCheck, dropMenu } = props;

  /**
   * SW2-2541 — read-only lists stop a new pick, but a legend item or note picked on the latest
   * version stays armed when the user switches to a locked previous version. Placing it there would fail
   * the whole save with a 403, so drop the pick when the lock comes on.
   */
  const legendLocked = !!mapEditorRouteContext?.legendLockedOnVersion;
  const activeType = interactionContext?.positionableType;
  const hasActivePick = interactionContext?.positionableSourceModel != null;

  useEffect(() => {
    if (legendLocked && hasActivePick && isMapVersionLockedType(activeType)) {
      interactionContext.setDefaultMode();
    }
    // interactionContext is a fresh object every render; the three values above are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legendLocked, hasActivePick, activeType]);

  if (!dataSourceContext || !interactionContext) {
    return null;
  }

  const {
    get,
    getDataTypeLabel,
    getDataTypes,
    isReadOnlyDataType,
  } = dataSourceContext;
  const {
    positionableType,
    setPositionableType,
    positionableSourceModel,
    setPositionableSourceModel,
    mode,
  } = interactionContext;

  // curry and switch args, for compat. with {toggle}
  const setMode = modeData => newMode => {
    if (mode === "readOnly") {
      return;
    }

    return interactionContext.setMode(newMode, modeData);
  };

  const dataSources = sortBy(
    mapEditorRouteContext.dataSources,
    "order",
  ).filter(positionable =>
    getDataTypes().includes(positionable.positionableType),
  );

  const classes = ["positionable-navigation", className || ""];

  return (
    <div className={classnames(classes)}>
      {dataSources.map((source, d) => {
        const dataType = source.positionableType;
        const isActive = positionableType === dataType;
        const items = get(dataType);
        
        if ( dataType === "findings" ) {
          items.sort((a, b) => b.id - a.id);
        }
        const label = positionableType2PageName[source.positionableType];
        const actions =
          label === "Annotations" || source.positionableType === "asset_type"
            ? actionsWithAllPerm
            : undefined;

        return (
          <PositionableList
            key={dataType}
            title={getDataTypeLabel(dataType)}
            type={dataType}
            items={items}
            toggleActive={forceActive =>
              toggle(
                forceActive === true ? "" : positionableType,
                dataType,
                setPositionableType,
              )
            }
            toggleActiveItem={itemIndex =>
              toggle(
                positionableSourceModel,
                itemIndex,
                setPositionableSourceModel,
              )
            }
            isActive={isActive}
            activeItemIndex={isActive ? positionableSourceModel : null}
            setInteractionMode={(newMode, modeData = null) =>
              toggle(mode, newMode, setMode(modeData))
            }
            permCheck={permCheck}
            label={label}
            actions={actions}
            enableSearch={source.enableSearch}
            mode={mode}
            // SW2-2541: a locked previous version takes no legend item or note adds, so those lists
            // go read-only there — no "+", and picking an item to place does nothing.
            readOnly={
              isReadOnlyDataType(dataType) ||
              (mapEditorRouteContext.legendLockedOnVersion && isMapVersionLockedType(dataType))
            }
            dropMenu={dropMenu}
          />
        );
      })}
      
      <CalibrateMap isOpen={(mapEditorRouteContext.showCalibrateMap && !mapEditorRouteContext.mapMobileView) || false} />
    </div>
  );
}

PositionableNavigation.propTypes = {
  className: PropTypes.string,
  permCheck: PropTypes.func.isRequired,
};

export default PositionableNavigation;
